import { adminPost } from "@/lib/kbAdminClient";
import type { BulkImportItem, KbAudience, KbScope, KbStatus, PanelNote } from "./types";
import { parseBulkImportItems } from "./parseBulkImportItems";

export type RunKbBulkImportContext = {
  bulkImportText: string;
  hasEditorName: boolean;
  articleAudience: KbAudience;
  articleScope: KbScope;
  articleDocType: string;
  articleStatus: KbStatus;
  canPublishKb: boolean;
  editorName: string;
  /** When true, sends confirmPolicyOverwrite on each article save (needed to update existing policy-classified rows). */
  allowPolicyOverwrite: boolean;
  showNote: (n: PanelNote | null) => void;
  setIsBulkImporting: (v: boolean) => void;
  refreshDraftCount: () => void | Promise<void>;
};

export async function runKbBulkImport(ctx: RunKbBulkImportContext): Promise<void> {
  const {
    bulkImportText,
    hasEditorName,
    articleAudience,
    articleScope,
    articleDocType,
    articleStatus,
    canPublishKb,
    editorName,
    allowPolicyOverwrite,
    showNote,
    setIsBulkImporting,
    refreshDraftCount,
  } = ctx;

  showNote(null);
  if (!hasEditorName) {
    showNote({ tone: "error", title: "Profile required", text: "Complete your signed-in profile name in Settings before importing articles." });
    return;
  }
  if (!bulkImportText.trim()) {
    showNote({ tone: "error", title: "Import data missing", text: "Paste JSON or load a JSON file before running the import." });
    return;
  }

  let items: BulkImportItem[];
  try {
    items = parseBulkImportItems(bulkImportText);
  } catch (error) {
    showNote({ tone: "error", title: "Import format error", text: error instanceof Error ? error.message : String(error) });
    return;
  }

  setIsBulkImporting(true);
  let successCount = 0;
  const failures: string[] = [];

  try {
    for (const [index, item] of items.entries()) {
      const response = await adminPost<any>("/api/kb/admin/article", {
        mode: "save",
        slug: item.slug || undefined,
        title: item.title,
        content: typeof item.content === "string" ? item.content : item.body,
        answerHint: item.answerHint ?? null,
        audience: item.audience ?? articleAudience,
        scope: item.scope ?? articleScope,
        doc_type: item.doc_type ?? (articleDocType || null),
        tags: Array.isArray(item.tags) ? item.tags : [],
        locations: Array.isArray(item.locations) ? item.locations : [],
        sourceUrl: item.sourceUrl ?? item.source_url ?? null,
        status: item.status ?? (canPublishKb ? articleStatus : "draft"),
        chunkSize: 500,
        overlap: 50,
        editedBy: editorName,
        ...(allowPolicyOverwrite ? { confirmPolicyOverwrite: true } : {}),
      });

      if (!response.ok) {
        failures.push(`#${index + 1} ${item.title}: ${response.error}`);
        continue;
      }
      successCount += 1;
    }
  } finally {
    setIsBulkImporting(false);
  }

  void refreshDraftCount();
  showNote({
    tone: failures.length ? "error" : "success",
    title: failures.length ? "Import finished with issues" : "Import complete",
    text: failures.length
      ? `Imported ${successCount}/${items.length} article(s). ${failures.join(" | ")}`
      : `Imported ${successCount} article(s).`,
  });
}
